import { useEffect, useState } from 'react';
import { useFeedbackStore } from '../stores/feedbackStore';

const StarIcon = ({ filled }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill={filled ? 'currentColor' : 'none'}>
    <path d="M8 1L10.29 5.68L15.5 6.44L11.75 10.09L12.64 15.28L8 12.77L3.36 15.28L4.25 10.09L0.5 6.44L5.71 5.68L8 1Z" stroke="currentColor" strokeWidth="1"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M3 8L6.5 11.5L13 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M2 4H14M5.333 4V2.667C5.333 2.298 5.632 2 6 2H10C10.368 2 10.667 2.298 10.667 2.667V4M12.667 4V13.333C12.667 13.702 12.368 14 12 14H4C3.632 14 3.333 13.702 3.333 13.333V4" stroke="currentColor" strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const RatingStars = ({ rating }) => (
  <div className="flex gap-0.5 text-warning">
    {[1, 2, 3, 4, 5].map((star) => (
      <StarIcon key={star} filled={star <= rating} />
    ))}
  </div>
);

const ConfirmModal = ({ isOpen, title, message, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onCancel}>
      <div className="bg-bg-secondary border border-border-color rounded-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-text-primary mb-2">{title}</h3>
        <p className="text-text-secondary mb-6">{message}</p>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-2.5 px-4 bg-bg-tertiary hover:bg-bg-primary text-text-primary rounded-lg font-medium transition-colors">
            Отмена
          </button>
          <button onClick={onConfirm} className="flex-1 py-2.5 px-4 bg-error hover:bg-red-600 text-white rounded-lg font-medium transition-colors">
            Удалить
          </button>
        </div>
      </div>
    </div>
  );
};

export const Feedback = () => {
  const { feedbacks, total, isLoading, status, fetchFeedback, markAsRead, deleteFeedback } = useFeedbackStore();
  
  const [filterStatus, setFilterStatus] = useState('all');
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, id: null });

  useEffect(() => {
    fetchFeedback({ limit: 50, offset: 0, status: filterStatus });
  }, [filterStatus]);

  const handleMarkAsRead = async (id) => {
    await markAsRead(id);
  };

  const handleDelete = (id) => {
    setConfirmModal({ isOpen: true, id });
  };

  const handleConfirmDelete = async () => {
    if (confirmModal.id) {
      await deleteFeedback(confirmModal.id);
      setConfirmModal({ isOpen: false, id: null });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-text-primary">Отзывы</h1>
        <div className="text-sm text-text-secondary">
          Всего: <span className="text-text-primary font-medium">{total}</span>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {[
          { value: 'all', label: 'Все' },
          { value: 'unread', label: 'Непрочитанные' },
          { value: 'read', label: 'Прочитанные' },
        ].map((s) => (
          <button
            key={s.value}
            onClick={() => setFilterStatus(s.value)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterStatus === s.value
                ? 'bg-accent-primary text-white'
                : 'bg-bg-secondary text-text-secondary hover:text-text-primary border border-border-color'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin h-8 w-8 border-2 border-accent-primary border-t-transparent rounded-full" />
          </div>
        ) : feedbacks.length === 0 ? (
          <div className="bg-bg-secondary rounded-xl border border-border-color p-12 text-center text-text-secondary">
            Отзывы не найдены
          </div>
        ) : (
          feedbacks.map((feedback) => (
            <div
              key={feedback.id}
              className={`bg-bg-secondary rounded-xl border border-border-color p-6 ${
                !feedback.has_submitted_feedback ? 'border-l-2 border-l-accent-primary' : ''
              }`}
            >
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex items-center gap-3">
                  <RatingStars rating={feedback.rating} />
                  <span className="text-sm text-text-secondary">
                    {new Date(feedback.created_at).toLocaleDateString('ru-RU', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {!feedback.has_submitted_feedback && (
                    <button
                      onClick={() => handleMarkAsRead(feedback.id)}
                      className="px-3 py-1.5 text-xs font-medium bg-success/10 text-success hover:bg-success/20 rounded-lg transition-colors flex items-center gap-1"
                    >
                      <CheckIcon />
                      Прочитать
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(feedback.id)}
                    className="p-1.5 text-text-secondary hover:text-error transition-colors"
                    title="Удалить"
                  >
                    <TrashIcon />
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <div className="text-sm text-text-secondary mb-1">
                  Пользователь: <span className="text-text-primary font-mono">{feedback.vk_user_id}</span>
                </div>
                {feedback.user_balance !== undefined && (
                  <div className="text-sm text-text-secondary">
                    Баланс: <span className="text-accent-primary">{feedback.user_balance} ⭐</span>
                    {feedback.user_disabled && (
                      <span className="ml-2 px-2 py-0.5 text-xs bg-error/10 text-error rounded">Заблокирован</span>
                    )}
                  </div>
                )}
              </div>

              {feedback.comment && (
                <div className="bg-bg-tertiary rounded-lg p-4 text-text-primary">
                  {feedback.comment}
                </div>
              )}

              {!feedback.comment && (
                <div className="text-text-muted text-sm italic">
                  Комментарий не указан
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title="Удалить отзыв?"
        message="Это действие нельзя отменить."
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmModal({ isOpen: false, id: null })}
      />
    </div>
  );
};
